import {
  formatCurrencyUsd,
  type EditableShippingPo,
} from "./shippingPoEditing.ts";

export interface PoChangeSnapshot {
  id: string;
  sequence: number;
  fields: Record<string, string>;
  lines: { sku: string; qty: number; price: string }[];
}

export interface PoTransfer {
  sku: string;
  quantity: number;
  source: string;
  destination: string;
}

export function snapshotPos(pos: EditableShippingPo[]): PoChangeSnapshot[] {
  return pos.map((po) => ({
    id: po.id,
    sequence: po.sequence,
    fields: {
      "Deliver To": po.deliverTo || "Not set",
      "Ship date": po.shipDateValue?.toLocaleDateString("en-US") ?? "Not set",
      "Arrival date": po.arrivalDate?.toLocaleDateString("en-US") ?? "Not set",
    },
    lines: po.lines.map((line) => ({
      sku: line.sku,
      qty: line.qty,
      price: formatCurrencyUsd(line.unitPriceUsd),
    })),
  }));
}

/** Reconcile unreported quantity changes against explicit PO-to-PO transfers. */
export function committedPoChanges(
  before: PoChangeSnapshot[],
  after: PoChangeSnapshot[],
  transfers: PoTransfer[]
): Record<string, string[]> {
  const changes: Record<string, string[]> = {};
  const allTransfers = [...transfers];
  for (const po of after) {
    const previous = before.find((candidate) => candidate.id === po.id);
    const entries: string[] = [];
    for (const [field, value] of Object.entries(po.fields)) {
      const oldValue = previous?.fields[field] ?? "Not set";
      if (value !== oldValue) entries.push(`${field}: ${oldValue} → ${value}`);
    }
    const skus = new Set([
      ...(previous?.lines.map((line) => line.sku) ?? []),
      ...po.lines.map((line) => line.sku),
    ]);
    for (const sku of skus) {
      const oldLine = previous?.lines.find((line) => line.sku === sku);
      const line = po.lines.find((candidate) => candidate.sku === sku);
      if (!oldLine && line)
        entries.push(`Added SKU ${sku} (${String(line.qty)} units).`);
      else if (oldLine && !line)
        entries.push(`Removed SKU ${sku} (${String(oldLine.qty)} units).`);
      else if (oldLine && line) {
        if (oldLine.qty !== line.qty)
          entries.push(
            `SKU ${sku} quantity: ${String(oldLine.qty)} → ${String(line.qty)}`
          );
        if (oldLine.price !== line.price)
          entries.push(`SKU ${sku} price: ${oldLine.price} → ${line.price}`);
      }
      const label = `PO ${String(po.sequence)}`;
      let recordedDelta = 0;
      for (const transfer of transfers) {
        if (transfer.sku !== sku) continue;
        if (transfer.destination === label) recordedDelta += transfer.quantity;
        if (transfer.source === label) recordedDelta -= transfer.quantity;
      }
      const residual = (line?.qty ?? 0) - (oldLine?.qty ?? 0) - recordedDelta;
      if (residual !== 0) {
        allTransfers.push({
          sku,
          quantity: Math.abs(residual),
          source: residual > 0 ? "Leftover inventory" : label,
          destination: residual > 0 ? label : "Leftover inventory",
        });
      }
    }
    changes[po.id] = entries;
  }
  for (const transfer of allTransfers) {
    const message = `SKU ${transfer.sku}: ${String(transfer.quantity)} units from ${transfer.source} to ${transfer.destination}.`;
    for (const po of after) {
      const label = `PO ${String(po.sequence)}`;
      if (transfer.source === label || transfer.destination === label) {
        changes[po.id]?.push(message);
      }
    }
  }
  return changes;
}
